import React, { useEffect, useRef, useState } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import { Dimmer, Form, Icon, IconProps, Loader } from 'semantic-ui-react';
import { useDynamicList } from 'ahooks';

import { getFileTypeInfo } from 'src/utils/helper';
import useUpload, { FileUpload, LikeFile } from 'src/hooks/useUpload';

export type Props = {
  module?: string;
  value: FileUpload | FileUpload[];
  onChange?: (file: FileUpload[], fileUploaded: FileUpload[]) => void;
  name?: string;
  placeholder?: string;
  label?: string;
  error?: string;
  onClear?: (file: FileUpload) => void;
  showFileList?: boolean;
  renderFileItem?: (file: FileUpload, index: number) => React.ReactNode;
  disabled?: boolean;
} & DropzoneOptions;

const DropzoneField: React.FC<Props> = ({
  value,
  onChange,
  name,
  module,
  label,
  placeholder,
  error,
  onClear,
  multiple = false,
  showFileList = true,
  renderFileItem,
  disabled,
  ...rest
}) => {
  const [loading, setLoading] = useState<boolean>();
  const { handleUpload } = useUpload();

  const files = useRef<FileUpload[]>(
    Array.isArray(value) ? value : value.url ? [value] : [],
  );

  useEffect(() => {
    files.current = Array.isArray(value) ? value : value.url ? [value] : [];
  }, [value]);

  const rejectedFiles = useDynamicList<FileRejection>([]);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: disabled || loading,
    multiple,
    async onDrop(acceptedFiles, fileRejections) {
      setLoading(true);

      rejectedFiles.resetList(fileRejections);

      if (acceptedFiles.length) {
        const fileUploaded = await Promise.all(
          acceptedFiles.map((file) => handleUpload(file, module)),
        );
        files.current = multiple
          ? [...files.current, ...fileUploaded]
          : fileUploaded;
        onChange?.(files.current, fileUploaded);
      }

      setLoading(false);
    },
    ...rest,
  });

  return (
    <Form.Field error={!!error}>
      <label>{label}</label>
      <div
        aria-disabled={disabled}
        {...getRootProps({ className: `iva_dropzone ${error ? 'error' : ''}` })}
      >
        <input {...getInputProps()} />
        <p className="text-center fs-18 text-gray">{placeholder}</p>
        {loading && (
          <Dimmer active inverted>
            <Loader size="medium">Uploading</Loader>
          </Dimmer>
        )}
      </div>

      {showFileList &&
        files.current?.map((file, index) => {
          if (renderFileItem) {
            return (
              <React.Fragment key={index}>
                {renderFileItem(file, index)}
              </React.Fragment>
            );
          }

          return (
            <FileItem
              key={index}
              file={file?.origin}
              url={file?.url}
              onClear={
                onClear
                  ? () => {
                      onClear?.({
                        origin: file?.origin!,
                        url: file?.url,
                      });

                      files.current = multiple
                        ? files.current.filter((f) => f.url !== file?.url)
                        : [];
                    }
                  : undefined
              }
            />
          );
        })}

      {showFileList &&
        rejectedFiles.list.map(({ file, errors }, index) => {
          return (
            <FileItem
              key={index}
              file={file}
              type="error"
              onClear={() => rejectedFiles.remove(index)}
            />
          );
        })}

      {error && <div className="ui pointing above prompt label">{error}</div>}
    </Form.Field>
  );
};

export type FileItemProps = {
  file?: File | LikeFile;
  onClear?: () => void;
  type?: 'success' | 'error' | 'loading';
  url?: string;
};

export const FileItem: React.FC<FileItemProps> = ({
  file,
  type,
  onClear,
  url,
}) => {
  const hasError = type === 'error';

  const iconProps: IconProps = {
    name: getFileTypeInfo(file).icon,
    color: hasError ? 'brown' : undefined,
  };

  return (
    <div className="d-flex-between py-4 cursor-pointer">
      <Icon {...iconProps} size="large" />
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`flex-1 ${hasError ? 'text-danger' : ''}`}
      >
        {file?.name}
      </a>
      {onClear && <Icon {...iconProps} name="times" onClick={onClear} />}
    </div>
  );
};

export default DropzoneField;
